import { Button, Modal, Select } from "antd";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Icon, { ICON_NAME } from "../../../../components/Icon";
import { checklistActions } from "../../../../util/store/checklist/reducer";
import {
    ACTION_TYPE,
    OPERATION_TYPE,
    operationType,
    propsModal,
    vesselTypes,
    zIndex
} from "../../../../util/varibles/constants";
import { IOperation, IVessel } from "../../../../util/varibles/interface";
import { IUpdateOp, selectPlan } from "../../constants";
import { planOpActions } from "../../reducer";
import SupportOp from "./_components/SupportOp";
import { SupportOperationProvider } from "./_components/SupportOp/SupportOpContext";
import { callEventEditOp, EditOperationProvider, getEditOp, useEditOp } from "./EditOperationContext";
import Event from "./Event";
import Harvest from "./Harvest";
import Service from "./Service";
import styles from "./style.module.scss";
import Transport from "./Transport";
import Treatment from "./Treatment";

interface IHeaderProps {
    isDrag?: boolean

    onClose(): void
}

interface IPropsTitle {
    vessel: IVessel
    editMode?: ACTION_TYPE
}

const EMPTY_OPERATION_TYPES: OPERATION_TYPE[] = [];

const defaultTitle = ({ vessel }: IPropsTitle) => {
    const { name, capacity } = vessel;
    const space = Math.round(capacity / 1000) / 1000;
    return name + (space ? `, ${space}t` : "");
}

const headerViews: { [id: string]: (props: IPropsTitle) => string } = {
    [OPERATION_TYPE.TREATMENT]: ({ vessel }: IPropsTitle) => {
        const { name, type } = vessel;
        const { shortName = '' } = vesselTypes[type - 1] || {};
        return [name, shortName].join(' - ');
    },
    [OPERATION_TYPE.EVENT]: ({ editMode }: IPropsTitle) => {
        return editMode === ACTION_TYPE.CREATE ? 'New event' : 'Edit event';
    }
}
const InitState = ({ opTypes }: { opTypes: Set<OPERATION_TYPE> }) => {
    const { getVesselTasks, getServiceTasks } = callEventEditOp();
    const getVesselTasksRef = useRef(getVesselTasks);
    const getServiceTasksRef = useRef(getServiceTasks);
    const [vessel] = useEditOp(state => state.vessel);
    const { id, operation_sub_types = [], capabilities = [] } = vessel || {};

    getVesselTasksRef.current = getVesselTasks;
    getServiceTasksRef.current = getServiceTasks;

    useEffect(() => {
        if (!id)
            return;
        if (opTypes.has(OPERATION_TYPE.TREATMENT)) {
            getVesselTasksRef.current({ id, operation_sub_types })
        }
        if (opTypes.has(OPERATION_TYPE.SERVICE)) {
            getServiceTasksRef.current({ id, capabilities })
        }
    }, [capabilities, id, opTypes, operation_sub_types]);

    return null;
}
export const Header: React.FC<IHeaderProps> = (props) => {
    const [vessel] = useEditOp(state => state.vessel);
    const [opType = OPERATION_TYPE.EVENT, set] = useEditOp(state => state.opType);
    const [supportMode] = useEditOp(state => state.supportMode);

    const { limitOperationTypes, isAllowChange = false, editMode } = useSelector(selectPlan.popupEdit);
    const stableLimitOperationTypes = limitOperationTypes || EMPTY_OPERATION_TYPES;
    const { icon } = operationType[opType];
    const view = headerViews[opType] || defaultTitle
    const opTypes = useMemo(() => new Set([...stableLimitOperationTypes, opType]), [stableLimitOperationTypes, opType]);

    return <div className={styles.header}>
        <InitState opTypes={opTypes} />
        <div className={styles.title}>
            <div className={styles.name}>
                <Icon icon={icon!} size='large' />
                {vessel && <span>{view({ vessel, editMode })}</span>}
            </div>
            {supportMode
                ? <div className={styles.name}>
                    <span>-</span>
                    <span>{operationType[opType]?.name}</span>
                    <Icon icon={ICON_NAME.ARROW_RIGHT_3} size='large' />
                    <span>Support vessel</span>
                </div>
                : ((isAllowChange && vessel) && <Select
                    classNames={{ popup: { root: styles.operationTypes } }}
                    className={styles.selectOpType}
                    value={opType}
                    variant="borderless"
                    onChange={value => set({ opType: value })}
                    popupMatchSelectWidth={false}
                    options={stableLimitOperationTypes.map((item: number | string) => ({
                        value: operationType[item].id,
                        label: operationType[item].name
                    }))}
                />)
            }
        </div>
        <div className='bt-close' onClick={props.onClose} />
    </div>;
};


const StyleBody: React.FC<{ rootRef: () => HTMLDivElement | null }> = ({ rootRef }) => {
    const [supportMode] = useEditOp(state => state.supportMode);
    useEffect(() => {
        const ref = rootRef();
        if (ref) {
            ref.style.height = supportMode ? "100vh" : "";
        }
    }, [rootRef, supportMode])

    return null;
}

interface IFooterProps {
    saveTxt?: string
    disableSave?: boolean
    loading?: boolean

    handleSubmit(): void

    handleBeforePlan?(callback?: (newOp?: IOperation) => void): void
}

export const Body: React.FC = () => {
    const [opType] = useEditOp(state => state.opType);
    const bodyRef = useRef<HTMLDivElement>(null);

    const Node = useMemo(() => views[`${opType}`], [opType]);

    return <div className="h-full max-h-[calc(100vh-80px)]" ref={bodyRef}>
        <StyleBody rootRef={() => bodyRef.current} />
        {!!Node && <Node />}
    </div>
}


export const Footer: React.FC<IFooterProps> = (props) => {
    const get = useRef(getEditOp());
    const [opType, setEditOp] = useEditOp(state => state.opType);
    const set = useRef(setEditOp);
    const [supportMode] = useEditOp(state => state.supportMode);

    const {
        disableSave,
        saveTxt = get.current().editMode === ACTION_TYPE.CREATE ? 'Plan' : 'Update',
        loading,
        handleSubmit,
        handleBeforePlan
    } = props;

    const SectionBySpport = useMemo(() => {
        const allowSp = new Set<OPERATION_TYPE | undefined>([OPERATION_TYPE.HARVEST, OPERATION_TYPE.TREATMENT]);
        const cancel = <Button className='bt-default' onClick={get.current().onClose}>Cancel</Button>;
        if (allowSp.has(opType)) {
            return supportMode
                ? <Button className='bt-default' onClick={() => set.current({ supportMode: false })}>Back</Button>
                : <>
                    <Button {...{
                        className: 'bt-primary',
                        type: 'primary',
                        loading,
                        onClick: () => {
                            if (handleBeforePlan)
                                handleBeforePlan(() => set.current({ supportMode: true }))
                            else
                                set.current({ supportMode: true })
                        },
                        disabled: disableSave
                    }}>
                        <span>Select support vessel</span>
                    </Button>
                    {cancel}
                </>
        }
        return cancel;
    }, [opType, supportMode, loading, disableSave, handleBeforePlan]);

    return <div className={styles.footer}>
        <Button {...{
            className: 'bt-primary',
            type: 'primary',
            loading,
            onClick: handleSubmit,
            disabled: disableSave
        }}>
            <span>{saveTxt}</span>
        </Button>
        {SectionBySpport}
    </div>;
};

const views: { [id: string]: React.FC } = {
    [OPERATION_TYPE.HARVEST]: Harvest,
    [OPERATION_TYPE.TREATMENT]: Treatment,
    [OPERATION_TYPE.EVENT]: Event,
    [OPERATION_TYPE.TRANSPORT]: Transport,
    [OPERATION_TYPE.SERVICE]: Service
}

const PopupOperation: React.FC = () => {
    const popupData = useSelector(selectPlan.popupEdit);
    const operations = useSelector(selectPlan.ops);
    const level = useSelector(selectPlan.level);
    const dispatch = useDispatch();
    const widthRef = useRef(500)

    const { action, isShow, operationType } = popupData;

    const handleClose = useCallback(() => {
        dispatch(planOpActions.togglePopupOperation({ isShow: false }));
    }, [dispatch]);

    const handleSave = useCallback((params: IUpdateOp) => {
        dispatch(planOpActions.updateOps(params))
        action?.();
        handleClose();
        // setTimeout(handleClose, 200);
    }, [dispatch, action, handleClose]);

    useEffect(() => {
        dispatch(checklistActions.gets());
    }, [dispatch])

    const width = useMemo(() => {
        if (isShow) {
            widthRef.current = operationType === OPERATION_TYPE.EVENT ? 500 : 1200;
        }
        return widthRef.current
    }, [isShow, operationType])

    return <Modal
        {...propsModal}
        keyboard={false}
        open={isShow}
        zIndex={zIndex.popupLev1}
        width={width}
        classNames={{
            body: 'flex flex-col'
        }}
        wrapClassName={styles.popup}
        styles={{ body: { padding: 0, height: '100%' } }}
        onCancel={handleClose}
    >
        <EditOperationProvider
            opType={popupData?.operationType}
            operation={popupData?.values?.operation}
            vessel={popupData?.values?.vessel}
            activity_log={popupData?.values?.activity_log}
            editMode={popupData.editMode}
            operations={operations}
            level={level}
            onSave={handleSave}
            onClose={handleClose}
        >
            <Header onClose={handleClose} />
            <Body />
            <SupportOperationProvider>
                <SupportOp />
            </SupportOperationProvider>
        </EditOperationProvider>
    </Modal>;
};

export default PopupOperation
